import { RightOutlined } from "@ant-design/icons";
import Skeleton from "components/UI/skeleton";
import { useMSGroupAdminsAll } from "queries/meetingSchedule";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

/** Inisial dari nama grup (maks 2 huruf) untuk avatar. */
const getGroupInitials = (name = "") =>
  name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const FirstStepSchedule = ({ dataForm, onChangeDataForm }) => {
  const { t } = useTranslation();

  const hasInitialGroups = Array.isArray(dataForm.groups);
  const { data, isLoading } = useMSGroupAdminsAll(
    { page: 1, per_page: 999 },
    {
      enabled: !hasInitialGroups && dataForm.isDoneInitiateData,
    }
  );

  const handleChangeFirstStep = useCallback(
    (value) => {
      const params = {
        admin_group_id: value.id,
        dataGroup: value,
      };
      if (value.auto_assign === 1 && !dataForm.isFixedAdmin) {
        params.schedule_admin_id = null;
      }
      onChangeDataForm(params);
    },
    [onChangeDataForm, dataForm]
  );

  const dataGroups = useMemo(
    () => (hasInitialGroups ? dataForm.groups : data?.data ?? []),
    [hasInitialGroups, dataForm.groups, data?.data]
  );

  const isReady = !isLoading && dataForm.isDoneInitiateData !== false;

  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    if (!isReady) return;
    if (dataGroups.length !== 1) return;
    if (autoAdvancedRef.current) return;
    autoAdvancedRef.current = true;
    handleChangeFirstStep(dataGroups[0]);
  }, [isReady, dataGroups, handleChangeFirstStep]);

  const Content = useMemo(() => {
    if (!isReady) return <Skeleton active />;
    if (dataGroups.length === 0) {
      return (
        <div className="text-center text-sm text-neutral-500">
          {t("meeting_schedule.admin_no_group_message")}
        </div>
      );
    }
    if (dataGroups.length === 1) {
      return <Skeleton active />;
    }
    return (
      <div className="mx-auto grid max-w-xl grid-cols-1 gap-3">
        {dataGroups.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleChangeFirstStep(item)}
            className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-neutral-200 bg-white py-3.5 pl-5 pr-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {/* Aksen warna grup */}
            <span
              className="absolute inset-y-0 left-0 w-1.5"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            {/* Avatar inisial */}
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: item.color }}
            >
              {getGroupInitials(item.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-neutral-900">
                {item.name}
              </span>
              {item.auto_assign === 1 && (
                <span className="text-xs text-neutral-500">
                  {t("meeting_schedule.auto_assign_admin")}
                </span>
              )}
            </span>
            <RightOutlined className="shrink-0 text-neutral-300 transition-colors group-hover:text-primary" />
          </button>
        ))}
      </div>
    );
  }, [dataGroups, isReady, handleChangeFirstStep, t]);

  return (
    <div>
      <div className="text-center text-xl font-semibold text-neutral-900">
        {t("meeting_schedule.title_first_step")}
      </div>
      <div className="mt-5">{Content}</div>
    </div>
  );
};

export default FirstStepSchedule;
